import { EntitySearchType } from '@api/api/entity-constants';
import { groupedSubClassMap } from '@api/api/timeseries-schema';
import { assetSubClassCategories as categories } from '@terminal/components/search-modal/assetSubClassCategories';
import { Chips } from '@terminal/components/search-modal/components/chips/Chips';
import { ListItem } from '@terminal/components/search-modal/components/list-item/ListItem';
import {
  addTitleAndSearchType,
  EntityOrThemeWithListTypes,
  filterAccessibleRecents,
  isEntityItem,
  isThemeItem,
  ListItemData,
} from '@terminal/components/search-modal/utils';
import { SearchModalBase } from '@terminal/components/search-modal-base/SearchModalBase';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useTagsSearchCriteria } from '@terminal/hooks/use-tags-search/use-tags-search-criteria';
import { useTagsSearch } from '@terminal/hooks/use-tags-search/useTagsSearch';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { useRecentList } from '@terminal/views/turbo-chart/hooks/use-recent-list/useRecentList';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { NoResults } from './components/NoResults';
import { useThemeSearch } from './hooks/use-theme-search/useThemeSearch';
import {
  entityTabs,
  getSearchAssetChipCategories,
  getThemeTypesForChip,
  getVisibleSearchAssetChips,
  isThemesChip,
} from './utils';

const assetSubClassCategories = getSearchAssetChipCategories(
  categories.filter(c => c.value !== 'economics')
);

export const SearchAssetWidget = () => {
  const { t } = useTranslation('widget');
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeChip, setActiveChip] = useState('');
  const currentQueryRef = useRef('');
  const isBasicUser = useSubscription(state => state.isBasicUser());

  const { navigateToPage } = useNavigateToAssetOverview();
  const { navigateToTheme } = useNavigateToThemeOverview();
  const { recentlyOpenedThemesOrEntities } = useRecentList();
  const filteredRecentlyOpenedThemesOrEntities = filterAccessibleRecents(
    recentlyOpenedThemesOrEntities,
    isBasicUser
  );
  const hasRecents = filteredRecentlyOpenedThemesOrEntities.length > 0;

  const isEntityTab = entityTabs.includes(activeChip);
  const isAllTab = activeChip === '';
  const isThemesTab = isThemesChip(activeChip);
  const shouldUseLookup = isEntityTab && !currentQueryRef.current;
  const themeTypes = getThemeTypesForChip(activeChip);

  const { criteria } = useTagsSearchCriteria({
    activeSubclassChip: activeChip,
  });
  const {
    tags: entitySearchResults,
    loading,
    query: searchQuery,
    searchTags,
  } = useTagsSearch({
    searchType: shouldUseLookup
      ? EntitySearchType.EntityLookup
      : EntitySearchType.EntityAndEconomies,
    params: {
      limit: 30,
      ...(shouldUseLookup ? criteria : {}),
    },
  });

  const showThemes = isThemesTab || (isAllTab && !!searchQuery);
  const {
    themeSearchResults,
    handleTextChange,
    isLoading: themesLoading,
  } = useThemeSearch(true, showThemes, themeTypes);

  const filteredEntitySearchResults = useMemo(() => {
    if (isThemesTab) {
      return [];
    }

    if (isAllTab || !searchQuery) {
      return entitySearchResults;
    }

    if (isEntityTab && activeChip in groupedSubClassMap) {
      const allowedSubClasses =
        groupedSubClassMap[activeChip as keyof typeof groupedSubClassMap];

      return entitySearchResults.filter(entity =>
        allowedSubClasses.includes(entity.sub_class)
      );
    }

    return entitySearchResults;
  }, [
    entitySearchResults,
    searchQuery,
    activeChip,
    isAllTab,
    isThemesTab,
    isEntityTab,
  ]);

  const showEntityResults = !!filteredEntitySearchResults.length;
  const themeSearchResultsIsVisible = !!themeSearchResults.length && showThemes;
  const isThemesLoading = showThemes && themesLoading;
  const isLoading = (!isThemesTab && loading) || isThemesLoading;

  const entitiesTitle = isAllTab ? t('widget:search.assets') : '';
  const themesTitle =
    isAllTab && (themeSearchResultsIsVisible || isThemesLoading)
      ? t('widget:search.investmentThemes')
      : '';

  const searchListData: ListItemData[] = useMemo(
    () => [
      {
        data: filteredRecentlyOpenedThemesOrEntities,
        searchListType: 'recent',
        isVisible: !searchQuery && !activeChip && hasRecents,
      },
      {
        title: entitiesTitle,
        data: filteredEntitySearchResults,
        searchListType: 'result',
        loading: loading && !showEntityResults,
        isVisible: !isThemesTab && (loading || showEntityResults),
      },
      {
        title: themesTitle,
        data: themeSearchResults,
        searchListType: 'result',
        loading: isThemesLoading && !themeSearchResultsIsVisible,
        isVisible: themeSearchResultsIsVisible || isThemesLoading,
        isCollapsibleSection: showEntityResults,
      },
    ],
    [
      showEntityResults,
      filteredRecentlyOpenedThemesOrEntities,
      searchQuery,
      filteredEntitySearchResults,
      activeChip,
      themeSearchResults,
      entitiesTitle,
      themesTitle,
      themeSearchResultsIsVisible,
      loading,
      isThemesTab,
      hasRecents,
      isThemesLoading,
    ]
  );

  const filteredData = useMemo(
    () =>
      searchListData
        .filter(e => e.isVisible)
        .flatMap(addTitleAndSearchType) as EntityOrThemeWithListTypes[],
    [searchListData]
  );

  useEffect(() => {
    setActiveChip(hasRecents ? '' : 'stocks');
  }, [hasRecents]);

  const onClose = () => {
    searchParams.delete(queryKeys.widget);
    setSearchParams(searchParams);
  };

  const handleSelectItem = (item: EntityOrThemeWithListTypes) => {
    if (isThemeItem(item)) {
      navigateToTheme({
        themeId: item.id,
        source: POSTHOG_TRACKING_SOURCES.SEARCH,
      });
    } else if (isEntityItem(item)) {
      navigateToPage({
        entityTag: item.tag,
        source: POSTHOG_TRACKING_SOURCES.SEARCH,
        searchParams,
      });
    }
  };

  const handleSearch = (value: string) => {
    currentQueryRef.current = value;
    searchTags(value);
    handleTextChange(value);
  };

  const handleChangeChip = (selectedChip: string) => {
    setActiveChip(selectedChip === activeChip ? '' : selectedChip);
  };

  const chipData = getVisibleSearchAssetChips(
    assetSubClassCategories,
    hasRecents
  );

  return (
    <SearchModalBase<EntityOrThemeWithListTypes>
      searchValue={searchQuery}
      value={searchQuery}
      handleSearch={handleSearch}
      selectItem={handleSelectItem}
      dropdownItems={filteredData}
      data-testid="search-asset-modal-widget"
      isOpen
      isLoading={isLoading}
      shouldResetScrollOnItemsChange={false}
      title={t('widget:search.dataSearchTitle')}
      onClose={onClose}
      searchInputVariant="secondary"
      showHeader={false}
      renderDropdownItem={(...args) => {
        const [item, , isFocused, , setFocusedText] = args;

        return (
          <ListItem
            item={item}
            isFocused={isFocused}
            setFocusedText={setFocusedText}
            searchQuery={searchQuery}
            companyCountTypes={['industry']}
            variant="secondary"
          />
        );
      }}
      beforeResultsNode={
        <Chips
          chipData={chipData}
          activeChip={activeChip}
          handleChangeChip={handleChangeChip}
          variant="secondary"
        />
      }
      placeholder={t('widget:search.placeholder')}
      noSearchResultsLabel={<NoResults />}
    />
  );
};
